/**
 * Блок «Имущество» — шесть разделов описи (ТЗ п. 5.2.7, FR-19…FR-21).
 *
 * Признак «есть имущество» управляет ТОЛЬКО формулировками заявления
 * (правила R-11/R-12). Опись формируется всегда и со всеми шестью разделами,
 * поэтому таблицы доступны независимо от признака (FR-20).
 */

import { useState } from 'react';
import {
  ABSENCE_CONFIRMATION_OPTIONS,
  CONFIRMATION_OPTIONS,
  DEFAULT_ABSENCE_CONFIRMATION,
  defaultConfirmation,
  newRowId,
} from '@bd/domain';
import type { BankAccount, PropertyScope, Realty, Security, ShareHolding, Valuable, Vehicle } from '@bd/domain';
import { DataGrid, type ColumnDef } from '../../../components/DataGrid.js';
import { CheckboxField, SelectField, kopecksToText, textToKopecks } from '../../../components/fields/index.js';
import type { BlockProps } from './types.js';
import { PropertyDocumentsImport } from './PropertyDocumentsImport.js';
import { withProperty } from './merge-imports.js';
import { PreviousNameHint } from './PreviousNameHint.js';
import { DebtorInnHint, type DocumentSubject } from './DebtorInnHint.js';

const OWNERSHIP_OPTIONS = ['индивидуальная', 'общая долевая', 'общая совместная'];
const OWNERSHIP_TO_CODE: Record<string, 'individual' | 'shared' | 'joint'> = {
  'индивидуальная': 'individual',
  'общая долевая': 'shared',
  'общая совместная': 'joint',
};
const CODE_TO_OWNERSHIP: Record<string, string> = {
  individual: 'индивидуальная',
  shared: 'общая долевая',
  joint: 'общая совместная',
};

const REALTY_KINDS: Record<string, string> = {
  land: 'Земельный участок',
  house: 'Жилой дом',
  flat: 'Квартира',
  dacha: 'Дача',
  garage: 'Гараж',
  other: 'Иное недвижимое имущество',
};

const VEHICLE_KINDS: Record<string, string> = {
  car: 'Легковой автомобиль',
  truck: 'Грузовой автомобиль',
  moto: 'Мототранспорт',
  agricultural: 'Сельхозтехника',
  water: 'Водный транспорт',
  air: 'Воздушный транспорт',
  other: 'Иное',
};

const VALUABLE_KINDS: Record<string, string> = {
  cash: 'Наличные денежные средства',
  jewelry: 'Драгоценности',
  art: 'Предметы искусства',
  professional: 'Имущество для профессиональных занятий',
  other: 'Иное ценное имущество',
};

/** Графа выбора подвида: в профиле хранится код, в таблице — наименование. */
function kindColumn<T extends { kind: string }>(titles: Record<string, string>, width = 12): ColumnDef<T> {
  const reverse = Object.fromEntries(Object.entries(titles).map(([code, title]) => [title, code]));
  return {
    key: 'kind',
    title: 'Подвид',
    width,
    type: 'select',
    options: Object.values(titles),
    get: (row) => titles[row.kind] ?? '',
    set: (row, value) => ({ ...row, kind: (reverse[value] ?? 'other') as T['kind'] }),
  };
}

function ownershipColumn<T extends { ownership: string; share?: string }>(): ColumnDef<T> {
  return {
    key: 'ownership',
    title: 'Вид собственности',
    width: 10,
    type: 'select',
    options: OWNERSHIP_OPTIONS,
    get: (row) => CODE_TO_OWNERSHIP[row.ownership] ?? '',
    set: (row, value) => ({ ...row, ownership: OWNERSHIP_TO_CODE[value] ?? 'individual' }),
  };
}

function costColumn<T extends { cost: number }>(title = 'Стоимость, ₽'): ColumnDef<T> {
  return {
    key: 'cost',
    title,
    width: 10,
    type: 'money',
    align: 'right',
    get: (row) => kopecksToText(row.cost),
    set: (row, value) => ({ ...row, cost: textToKopecks(value) }),
  };
}

/**
 * «Чем подтверждается» — документ к абзацу объекта в заявлении
 * (ТЗ корректировок, п. 5). Пока юрист не выбрал свой, в ячейке виден
 * документ по умолчанию: так сразу понятно, что уйдёт в заявление.
 */
function confirmationColumn<T extends { kind: string; confirmation?: string }>(section: 'realty' | 'vehicle'): ColumnDef<T> {
  return {
    key: 'confirmation',
    title: 'Чем подтверждается',
    width: 11,
    type: 'combo',
    options: CONFIRMATION_OPTIONS,
    get: (row) => row.confirmation || defaultConfirmation(section, row.kind),
    set: (row, value) => ({ ...row, confirmation: value }),
  };
}

const SCOPE_TITLES: Record<PropertyScope, string> = {
  both: 'недвижимое и движимое',
  realty: 'только недвижимое',
  movable: 'только движимое',
};
const SCOPE_CODES = Object.fromEntries(Object.entries(SCOPE_TITLES).map(([code, title]) => [title, code])) as Record<string, PropertyScope>;

const text = <T,>(key: keyof T & string, title: string, width = 14): ColumnDef<T> => ({
  key,
  title,
  width,
  get: (row) => (row[key] as string) ?? '',
  set: (row, value) => ({ ...row, [key]: value }),
});

type SectionId = 'realty' | 'vehicles' | 'accounts' | 'shares' | 'securities' | 'valuables';

const SECTION_TITLES: Array<[SectionId, string]> = [
  ['realty', 'I. Недвижимое имущество'],
  ['vehicles', 'II. Движимое имущество'],
  ['accounts', 'III. Счета в банках'],
  ['shares', 'IV. Акции и участие в организациях'],
  ['securities', 'V. Иные ценные бумаги'],
  ['valuables', 'VI. Наличные и иное ценное имущество'],
];

export function PropertyBlock({ profile, update, readOnly, errorFor }: BlockProps) {
  const [section, setSection] = useState<SectionId>('realty');
  /** ФИО должника в последних загруженных выписках — для подсказки о прежней фамилии */
  const [documentNames, setDocumentNames] = useState<string[]>([]);
  const [documentSubjects, setDocumentSubjects] = useState<DocumentSubject[]>([]);
  const property = profile.property;

  /**
   * Новый состав раздела считается от АКТУАЛЬНОГО состояния профиля,
   * а не от того, что таблица видела при отрисовке: два быстрых изменения
   * иначе строятся от одного и того же списка, и второе затирает первое.
   */
  const setRows = <K extends SectionId>(key: K, apply: (previous: (typeof property)[K]) => (typeof property)[K]) =>
    update((p) => ({ ...p, property: { ...p.property, [key]: apply(p.property[key]) } }));

  const counts: Record<SectionId, number> = {
    realty: property.realty.length,
    vehicles: property.vehicles.length,
    accounts: property.accounts.length,
    shares: property.shares.length,
    securities: property.securities.length,
    valuables: property.valuables.length,
  };

  return (
    <section className="block">
      <h2>Имущество</h2>

      <PropertyDocumentsImport
        caseId={profile.caseId}
        property={property}
        deals={profile.deals.items}
        debtorName={[profile.debtor.last, profile.debtor.first, profile.debtor.middle].filter(Boolean).join(' ')}
        debtorInn={profile.debtor.inn}
        readOnly={readOnly}
        onAccept={(accepted) => update((p) => withProperty(p, accepted))}
        onNames={setDocumentNames}
        onDocuments={setDocumentSubjects}
      />
      <PreviousNameHint names={documentNames} profile={profile} update={update} readOnly={readOnly} />
      <DebtorInnHint documents={documentSubjects} profile={profile} update={update} readOnly={readOnly} />

      <CheckboxField
        label="За должником зарегистрировано имущество"
        checked={property.hasProperty}
        readOnly={readOnly}
        onChange={(v) => update((p) => ({ ...p, property: { ...p.property, hasProperty: v } }))}
      />
      <p className="hint">
        Признак влияет только на формулировки заявления (правила R-11 и R-12). Опись имущества формируется всегда
        и со всеми шестью разделами; незаполненные строки выводятся с прочерком.
      </p>
      {errorFor('property.hasProperty') && <p className="warning">{errorFor('property.hasProperty')}</p>}

      {/*
        Фразы заявления «не зарегистрировано … имущество» и «Иного …
        имущества не зарегистрировано» (ТЗ корректировок, 16.10.2026).
        Документ пишется так, как он встанет после «подтверждаются …».
      */}
      <div className="row row-2">
        <SelectField
          label="О каком имуществе писать в заявлении"
          value={SCOPE_TITLES[property.scope ?? 'both']}
          options={Object.values(SCOPE_TITLES)}
          allowCustom={false}
          readOnly={readOnly}
          onChange={(v) =>
            update((p) => ({ ...p, property: { ...p.property, scope: SCOPE_CODES[v] ?? 'both' } }))
          }
        />
        <SelectField
          label={property.hasProperty ? 'Чем подтверждается отсутствие иного имущества' : 'Чем подтверждается отсутствие имущества'}
          value={property.absenceConfirmation ?? DEFAULT_ABSENCE_CONFIRMATION}
          options={ABSENCE_CONFIRMATION_OPTIONS}
          readOnly={readOnly}
          onChange={(v) => update((p) => ({ ...p, property: { ...p.property, absenceConfirmation: v } }))}
        />
      </div>

      <nav className="tabs">
        {SECTION_TITLES.map(([id, title]) => (
          <button
            key={id}
            type="button"
            className={section === id ? 'tab tab-active' : 'tab'}
            onClick={() => setSection(id)}
          >
            {title}
            {counts[id] > 0 && <span className="badge">{counts[id]}</span>}
          </button>
        ))}
      </nav>

      {section === 'realty' && (
        <DataGrid<Realty>
          rows={property.realty}
          readOnly={readOnly}
          getRowId={(row) => row.id}
          onChange={(apply) => setRows('realty', apply)}
          createRow={() => ({
            id: newRowId(), kind: 'flat', cadastralNumber: '', address: '', area: null,
            ownership: 'individual', acquisitionBasis: '', cost: 0,
          })}
          columns={[
            kindColumn<Realty>(REALTY_KINDS),
            text<Realty>('cadastralNumber', 'Кадастровый номер', 11),
            text<Realty>('address', 'Адрес', 16),
            {
              key: 'area', title: 'Площадь, кв. м', width: 6, type: 'number', align: 'right',
              get: (row) => (row.area ?? '') as never,
              set: (row, value) => ({ ...row, area: value ? Number(value.replace(',', '.')) : null }),
            },
            ownershipColumn<Realty>(),
            text<Realty>('share', 'Доля', 5),
            text<Realty>('acquisitionBasis', 'Основание приобретения', 12),
            costColumn<Realty>(),
            text<Realty>('pledge', 'Залог', 7),
            confirmationColumn<Realty>('realty'),
          ]}
        />
      )}

      {section === 'vehicles' && (
        <DataGrid<Vehicle>
          rows={property.vehicles}
          readOnly={readOnly}
          getRowId={(row) => row.id}
          onChange={(apply) => setRows('vehicles', apply)}
          createRow={() => ({
            id: newRowId(), kind: 'car', model: '', year: null, identificationNumber: '',
            ownership: 'individual', storagePlace: '', acquisitionBasis: '', cost: 0,
          })}
          columns={[
            kindColumn<Vehicle>(VEHICLE_KINDS, 11),
            text<Vehicle>('model', 'Марка и модель', 11),
            {
              key: 'year', title: 'Год', width: 5, type: 'number', align: 'right',
              get: (row) => (row.year ?? '') as never,
              set: (row, value) => ({ ...row, year: value ? Number(value) : null }),
            },
            text<Vehicle>('identificationNumber', 'Идентификационный номер', 12),
            // Печатается в заявлении (ТЗ корректировок, п. 7); в опись не идёт
            text<Vehicle>('registrationNumber', 'Гос. рег. знак', 9),
            ownershipColumn<Vehicle>(),
            text<Vehicle>('share', 'Доля', 5),
            text<Vehicle>('storagePlace', 'Место хранения', 8),
            text<Vehicle>('acquisitionBasis', 'Основание приобретения', 8),
            costColumn<Vehicle>(),
            text<Vehicle>('pledge', 'Залог', 5),
            confirmationColumn<Vehicle>('vehicle'),
          ]}
        />
      )}

      {section === 'accounts' && (
        <DataGrid<BankAccount>
          rows={property.accounts}
          readOnly={readOnly}
          getRowId={(row) => row.id}
          onChange={(apply) => setRows('accounts', apply)}
          createRow={() => ({
            id: newRowId(), bankName: '', bankAddress: '', accountKind: '', currency: 'RUB',
            openedAt: null, balance: 0, cost: 0,
          })}
          columns={[
            text<BankAccount>('bankName', 'Наименование банка', 20),
            text<BankAccount>('bankAddress', 'Адрес банка', 22),
            text<BankAccount>('accountKind', 'Вид счёта', 14),
            text<BankAccount>('currency', 'Валюта', 8),
            {
              key: 'openedAt', title: 'Дата открытия', width: 10, type: 'date',
              get: (row) => row.openedAt ?? '',
              set: (row, value) => ({ ...row, openedAt: value || null }),
            },
            {
              key: 'balance', title: 'Остаток, ₽', width: 12, type: 'money', align: 'right',
              get: (row) => kopecksToText(row.balance),
              set: (row, value) => ({ ...row, balance: textToKopecks(value) }),
            },
          ]}
        />
      )}

      {section === 'shares' && (
        <DataGrid<ShareHolding>
          rows={property.shares}
          readOnly={readOnly}
          getRowId={(row) => row.id}
          onChange={(apply) => setRows('shares', apply)}
          createRow={() => ({
            id: newRowId(), organizationName: '', ogrn: '', inn: '', address: '',
            participationShare: '', acquisitionBasis: '', cost: 0,
          })}
          columns={[
            text<ShareHolding>('organizationName', 'Наименование организации', 22),
            text<ShareHolding>('ogrn', 'ОГРН', 12),
            text<ShareHolding>('inn', 'ИНН', 12),
            text<ShareHolding>('address', 'Адрес', 20),
            text<ShareHolding>('participationShare', 'Доля участия', 10),
            text<ShareHolding>('acquisitionBasis', 'Основание участия', 14),
          ]}
        />
      )}

      {section === 'securities' && (
        <DataGrid<Security>
          rows={property.securities}
          readOnly={readOnly}
          getRowId={(row) => row.id}
          onChange={(apply) => setRows('securities', apply)}
          createRow={() => ({ id: newRowId(), kind: '', issuer: '', nominal: 0, quantity: null, acquisitionBasis: '', cost: 0 })}
          columns={[
            text<Security>('kind', 'Вид ценной бумаги', 18),
            text<Security>('issuer', 'Лицо, выпустившее бумагу', 24),
            {
              key: 'nominal', title: 'Номинал, ₽', width: 12, type: 'money', align: 'right',
              get: (row) => kopecksToText(row.nominal),
              set: (row, value) => ({ ...row, nominal: textToKopecks(value) }),
            },
            {
              key: 'quantity', title: 'Количество', width: 10, type: 'number', align: 'right',
              get: (row) => (row.quantity ?? '') as never,
              set: (row, value) => ({ ...row, quantity: value ? Number(value) : null }),
            },
            costColumn<Security>('Общая стоимость, ₽'),
          ]}
        />
      )}

      {section === 'valuables' && (
        <DataGrid<Valuable>
          rows={property.valuables}
          readOnly={readOnly}
          getRowId={(row) => row.id}
          onChange={(apply) => setRows('valuables', apply)}
          createRow={() => ({ id: newRowId(), kind: 'cash', description: '', storagePlace: '', ownership: 'individual', cost: 0 })}
          columns={[
            kindColumn<Valuable>(VALUABLE_KINDS, 18),
            text<Valuable>('description', 'Описание', 30),
            ownershipColumn<Valuable>(),
            text<Valuable>('storagePlace', 'Место хранения', 20),
            costColumn<Valuable>(),
          ]}
        />
      )}
    </section>
  );
}
